import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { goTo } from '@adornis/router/client/open-href.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/fonts.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { of, switchMap, tap } from 'rxjs';

@customElement('campus-component-attachements')
export class CampusComponentAttachements extends ChemistryLitElement {
  @property({ type: Boolean, reflect: true }) showDeletion = false;
  @property({ type: Boolean, reflect: true }) showDownload = false;
  @property({ type: Boolean, reflect: true }) showUpload = false;

  @state() fileIDs = new RXController<string[]>(this, []);
  @state() private readonly _loading = new RXController(this, true);
  @state() private readonly _files = new RXController(
    this,
    this.fileIDs.observable.pipe(
      tap(_ => this._loading.next(true)),
      switchMap(ids =>
        ids.length !== 0 ? AdornisFile.getByID<AdornisFile>(ids)(AdornisFile.allFields) : of([] as AdornisFile[]),
      ),
      tap(_ => this._loading.next(false)),
    ),
  );
  @state() private readonly _errorMsg = new RXController<string>(this, '');

  override render() {
    if (this._loading.value) return html`<cr-campus-loader></cr-campus-loader>`;
    return html`
      <cr-flex space="md">
        <cr-h3>Anhänge</cr-h3>
        ${this._files.value?.length !== 0
          ? html`${repeat(
              this._files.value ?? [],
              file => file._id,
              (file, i) => html`${this._getFileDescLine(file, i)}`,
            )}`
          : html`<cr-text>Kein Anhang vorhanden</cr-text>`}
        ${this.showUpload ? html`${this._getUploadButton()}` : nothing}
      </cr-flex>
    `;
  }

  private _getFileDescLine(file: AdornisFile, index: number) {
    return html`
      <cr-card>
        <cr-flex horizontal space-between padding="sm" crossaxis-center>
          <cr-flex space="md">
            <cr-text ${css({ opacity: 0.3 })}> Anhang #${index + 1} </cr-text>
            <cr-h4 ${css({ wordBreak: 'break-all' })}>${file.meta?.fileName}</cr-h4>
          </cr-flex>
          <cr-flex horizontal space="sm" crossaxis-center>
            ${this.showDownload
              ? html`
                  <cr-button
                    icon="file_download"
                    @click=${() => goTo(file.getDownloadLink(), { target: '_blank' })}
                    ${css({ cursor: 'pointer', zoom: 0.8, marginLeft: this.spacing.md })}
                  >
                  </cr-button>
                `
              : nothing}
            ${this.showDeletion ? html`${this._getDeletionButton(file)}` : nothing}
          </cr-flex>
        </cr-flex>
      </cr-card>
    `;
  }

  private _getUploadButton() {
    return html`
      <cr-flex space="md">
        ${this._errorMsg.value !== ''
          ? html`<cr-text ${css({ color: 'red', textAlign: 'center' })}>${this._errorMsg.value}</cr-text>`
          : nothing}
        <cr-button
          mode="outline"
          @click=${async () => {
            const file = await askForFile({});
            if (!file?.[0]) {
              this._errorMsg.next('Beim Upload ist etwas schiefgegangen!');
              return;
            }
            const fileID = await AdornisFile.upload(file[0]);
            const event = new CustomEvent('fileUploaded', { detail: { fileID } });
            this.dispatchEvent(event);
          }}
        >
          Anhang hochladen
        </cr-button>
      </cr-flex>
    `;
  }

  private _getDeletionButton(file: AdornisFile) {
    return html`
      <cr-button
        mode="icon"
        icon="close"
        tone="error"
        @click=${async () => {
          if (!(await ChromiumDialog.confirm('Anhang löschen?'))) return;
          const id = file._id;
          await file.remove().catch(e => {
            throw new Error('Could not remove file, this should not happen!');
          });
          const event = new CustomEvent('fileDeleted', {
            detail: {
              fileID: id,
            },
          });
          this.dispatchEvent(event);
        }}
        ${css({ cursor: 'pointer' })}
      >
      </cr-button>
    `;
  }
}
